import React, { useCallback } from 'react';
import { GenericModal } from 'src/components/Modals/GenericModal';
import { create } from 'zustand';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import colors from 'src/utils/colors';
import { useSnackbar, useMetricFormatter, useAppDispatch } from 'src/utils/Hooks';
import { updateCampaign } from 'src/components/AdManager/AmsUtils';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { fetchMainEntity } from 'src/store/modules/entityService/operations';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';

const initialState = {
  open: false,
  entity: null,
  budgets: {
    current: 0,
    new: 0
  }
};

export const useExceededBudgetModal = create((set) => ({
  ...initialState,
  setOpen: () => set({ open: true }),
  setBudgets: (budgets: { current: number; new: number }) => set({ budgets }),
  onClose: () => set({ ...initialState }),
  setEntity: (entity: any) => set({ entity })
}));

export const ExceededBudgetModal = () => {
  const { showSnackbar } = useSnackbar();
  const { open, budgets, onClose, entity } = useExceededBudgetModal();
  const formatMetric = useMetricFormatter();
  const dispatch = useAppDispatch();

  const handleSubmit = useCallback(() => {
    try {
      updateCampaign(entity);
      showSnackbar({
        message: 'Campaign moved successfully',
        type: 'success'
      });
      dispatch(fetchMainEntity('adCampaign', entity.id, entity.extendedAttributes?.retailerId));
    } catch (error) {
      showSnackbar({
        message: 'An error occurred. Please reach out to support@stackline.com for further guidance.',
        type: 'error'
      });
    } finally {
      onClose();
    }
  }, [dispatch, entity, onClose, showSnackbar]);

  return (
    <GenericModal
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiPaper-root': {
          width: '500px',
          borderRadius: '8px',
          padding: '24px 24px 22px 24px',
          overflow: 'hidden',
          height: '178px'
        },

        '.genericModal__header': {
          marginBottom: '12px'
        },
        '.genericModal__body': {
          height: '100%'
        }
      }}
    >
      <GenericModal.Header title="Move campaign to selected portfolio" onClose={onClose} />
      <GenericModal.Body>
        <Text
          variant="body2"
          sx={{
            lineHeight: '20px'
          }}
        >
          By moving this campaign into the selected portfolio, the MTD spend of{' '}
          {formatMetric(budgets.new, METRICTYPE.MONEY, {
            decimalPlaces: 1
          })}{' '}
          now exceeds the budget of{' '}
          {formatMetric(budgets.current, METRICTYPE.MONEY, {
            decimalPlaces: 1
          })}
          . Would you like to proceed?
        </Text>
        <Flex
          gap={15}
          justifyContent="flex-end"
          sx={{
            marginTop: 'auto'
          }}
        >
          <SlButton
            onClick={() => {
              onClose();
            }}
            buttonSx={{
              border: `1px solid ${colors.graysSecondary}`,
              backgroundColor: 'white',
              padding: '6px 20px',
              '&:hover': {
                backgroundColor: 'white'
              }
            }}
            textSx={{
              color: colors.darkBlue
            }}
          >
            Cancel
          </SlButton>
          <SlButton
            onClick={async () => {
              handleSubmit();
            }}
            buttonSx={{
              backgroundColor: colors.darkBlue,
              border: `1px solid ${colors.darkBlue}`,
              padding: '6px 20px',
              '&:hover': {
                backgroundColor: colors.darkBlue
              }
            }}
            textSx={{
              color: colors.white
            }}
          >
            Confirm
          </SlButton>
        </Flex>
      </GenericModal.Body>
    </GenericModal>
  );
};
